import React, { useState } from "react"
import * as ReactIs from "react-is"
import { useLocation, useNavigationType, Navigate } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import useSSR from "use-ssr"
import { Helmet } from "react-helmet"
import { Flex, Loader } from "@capioweb/ui-components/src"
import { NotFound404 } from "../../pages"
import {
	PUBLIC_API_ENDPOINT,
	queryRefetchInterval,
	queryStaleTime,
} from "../../constants"
import { getContent } from "../../api/content"
import {
	Alert,
	getContentByType,
	SiteSettingsProps,
} from "../../components/Core"
import { AppContext, EpiContext, UIContext } from "../../context"
import {
	useEpiEditMode,
	usePageQuery,
	useAnchorLinkScrolling,
} from "../../hooks"
import { QuickNavigatorEpi } from "../../components"
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const PageAndBlockResolver: React.FC = () => {
	const location = useLocation()
	const navigationType = useNavigationType()
	const appContext = React.useContext(AppContext)
	const [trackedPageViews, setTrackedPageView] = useState([""])

	const { isServer } = useSSR()

	const path = location.pathname + location.search

	// Site settings query
	const { data: siteSettingsData } = useQuery<SiteSettingsProps>(
		"site",
		() => getContent(`${PUBLIC_API_ENDPOINT}/settings/site/`),
		{
			staleTime: queryStaleTime,
			refetchInterval: queryRefetchInterval,
		}
	)

	// Page query
	const { editMode } = React.useContext(EpiContext)
	const { data, isError, isLoading, error } = usePageQuery(path, {
		enabled: !editMode.isIdle,
		staleTime: queryStaleTime,
	})

	// Refetch menu query to ensure it stays up to date on page navigation
	const queryClient = useQueryClient()
	queryClient.refetchQueries(["menu"], { exact: true, active: true })

	useEpiEditMode(path, data)

	const { setPageAlerts } = React.useContext(UIContext)
	React.useEffect(() => {
		if (!isError && !isLoading) {
			/* Set page alerts to context */
			if (data?.alerts?.length > 0) {
				const alertsObj = data.alerts.reduce<
					Record<"common" | "local", Alert[]>
				>(
					(acc, alert) => {
						const key = alert.isGlobal ? "common" : "local"

						acc[key] = [...acc[key], alert]

						return acc
					},
					{
						common: [],
						local: [],
					}
				)

				setPageAlerts(alertsObj)
			} else {
				setPageAlerts({ common: [], local: [] })
			}

			// Set page id to context
			appContext.setPageId(data?.contentLink.id.toString() ?? "")
		}
	}, [data, isLoading, isError])

	// reset scroll position to 0 after page load
	React.useEffect(() => {
		if (!isServer && navigationType !== "POP") {
			const timer = setTimeout(() => window.scrollTo(0, 0), 10)
			return () => {
				clearTimeout(timer)
			}
		}
	}, [location, isServer, navigationType])

	useAnchorLinkScrolling()

	if (error && Number(error.message) === 404) {
		return <NotFound404 />
	}

	if (data?.modelType) {
		const page = React.createElement(getContentByType(data.modelType), data)

		if (ReactIs.isFragment(page)) {
			return <NotFound404 />
		}
	}

	appContext.cookiebotId =
		siteSettingsData?.cookiebotID ?? "65f3c763-5ad7-4429-84ae-ff66af9fbd25"

	if (trackedPageViews.indexOf(path) === -1 && data && siteSettingsData?.applicationInsightsKey) {
		trackedPageViews.push(path);
		setTrackedPageView(trackedPageViews)
		const appInsights = new ApplicationInsights({
			config: {
				instrumentationKey: siteSettingsData?.applicationInsightsKey ?? ""
			},
		});

		appInsights.loadAppInsights();

		appInsights.trackPageView({ uri: path, name: data?.meta?.title });
	}

	return (
		<>
			{data && siteSettingsData && (
				<Helmet htmlAttributes={{ lang: siteSettingsData.language }}>
					<title>{data.meta?.title || ""}</title>
					<meta property="og:title" content={data.meta?.title || ""} />
					<meta name="description" content={data.meta?.description || ""} />
					<meta
						property="og:description"
						content={data.meta?.description || ""}
					/>
					<meta
						property="og:image"
						content={
							(data.meta?.ogImage ||
								siteSettingsData?.siteFallbackSocialMediaUrl)
						}
					/>
					<meta
						property="og:url"
						content={data.meta?.externalUrl}
					/>
					{data.meta?.indexingSettings && (
						<meta name="robots" content={data.meta.indexingSettings} />
					)}
					<script
						id="Cookiebot"
						src="https://consent.cookiebot.com/uc.js"
						data-cbid={appContext.cookiebotId}
						type="text/javascript"
						data-blockingmode="auto"
					></script>
				</Helmet>
			)}
			{isLoading && !data ? (
				<Flex
					style={{ width: "100vw", height: "100vh" }}
					justifyContent="center"
					alignItems="center"
				>
					<Loader />
				</Flex>
			) : (
				<>
					{data?.redirectUrl && <Navigate to={data.redirectUrl} />}
					<Flex as="main" id="main" display="block">
						{/* Render page by modelType */}
						{data.modelType &&
							React.createElement(getContentByType(data.modelType), data)}

						{/* Epi admin QuickNavigator */}
						<QuickNavigatorEpi
							quickNavigatorLinks={data?.quickNavigatorLinks}
						/>
					</Flex>
				</>
			)}
		</>
	)
}

export { PageAndBlockResolver }
